import { z } from 'zod';
import {
  ABSENCE_LEVELS,
  AUDIT_ENTITIES,
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  ENROLLMENT_STATUSES,
  INTERNSHIP_STATUSES,
  SESSION_KINDS,
} from '@repo/shared';

/**
 * URL search params of the routes. Kept apart from the pages so the router does not import them eagerly:
 * every page is a lazy chunk (`routes/router.tsx`).
 */
const page = z.coerce.number().int().min(0).optional().catch(undefined);
const pageSize = z.coerce.number().int().optional().catch(undefined);
const text = z.string().optional().catch(undefined);
const uuid = z.uuid().optional().catch(undefined);

export const ABSENCE_LEVEL_FILTERS = ['all', ...ABSENCE_LEVELS] as const;
export const AbsencesSearchSchema = z.object({
  level: z.enum(ABSENCE_LEVEL_FILTERS).optional().catch(undefined),
  page,
  sort: text,
});
export type AbsencesSearch = z.infer<typeof AbsencesSearchSchema>;

export const GradeSheetsSearchSchema = z.object({
  session: z.enum(SESSION_KINDS).optional().catch(undefined),
  status: z.enum(['brouillon', 'transmis']).optional().catch(undefined),
  levelId: uuid,
});
export type GradeSheetsSearch = z.infer<typeof GradeSheetsSearchSchema>;

export const INTERNSHIP_TABS = ['list', 'sessions', 'types'] as const;
export const InternshipsSearchSchema = z.object({
  tab: z.enum(INTERNSHIP_TABS).optional().catch(undefined),
  status: z.enum(INTERNSHIP_STATUSES).optional().catch(undefined),
  typeId: uuid,
  role: z.enum(['supervisor', 'requests', 'jury']).optional().catch(undefined),
  q: text,
  page,
});
export type InternshipsSearch = z.infer<typeof InternshipsSearchSchema>;

export const GradeReviewsSearchSchema = z.object({
  status: z.enum(['answered', 'all']).optional().catch(undefined),
  sheet: uuid,
});
export type GradeReviewsSearch = z.infer<typeof GradeReviewsSearchSchema>;

export const StudentsSearchSchema = z.object({
  q: text,
  programId: uuid,
  levelId: uuid,
  groupId: uuid,
  status: z
    .enum([...ENROLLMENT_STATUSES, 'none'])
    .optional()
    .catch(undefined),
  page,
  pageSize,
  sort: text,
});
export type StudentsSearch = z.infer<typeof StudentsSearchSchema>;

export const STUDENT_TABS = ['home', 'notes', 'edt', 'pay', 'more'] as const;
export const STUDENT_MORE = ['abs', 'docs', 'stages', 'cal', 'ann'] as const;
export type MoreSection = (typeof STUDENT_MORE)[number];
export const StudentSearchSchema = z.object({
  tab: z.enum(STUDENT_TABS).optional().catch(undefined),
  more: z.enum(STUDENT_MORE).optional().catch(undefined),
});
export type StudentSearch = z.infer<typeof StudentSearchSchema>;

export const CANDIDATURE_TABS = ['all', 'submitted', 'review', 'interview', 'accepted', 'refused'] as const;
export const CandidaturesSearchSchema = z.object({
  tab: z.enum(CANDIDATURE_TABS).optional().catch(undefined),
  q: text,
  programId: uuid,
  since: z.iso.date().optional().catch(undefined),
  page,
  pageSize,
  sort: text,
  open: uuid,
});
export type CandidaturesSearch = z.infer<typeof CandidaturesSearchSchema>;

export const UnpaidSearchSchema = z.object({
  q: text,
  programId: uuid,
  minDays: z.coerce.number().int().optional().catch(undefined),
  page,
  pageSize,
  sort: text,
});
export type UnpaidSearch = z.infer<typeof UnpaidSearchSchema>;

export const SchedulesSearchSchema = z.object({
  student: uuid,
  status: z.enum(['pending']).optional().catch(undefined),
  page,
});
export type SchedulesSearch = z.infer<typeof SchedulesSearchSchema>;

export const DocumentsSearchSchema = z.object({
  q: text,
  type: z.enum(DOCUMENT_TYPES).optional().catch(undefined),
  status: z.enum(DOCUMENT_STATUSES).optional().catch(undefined),
  page,
  pageSize,
  sort: text,
});
export type DocumentsSearch = z.infer<typeof DocumentsSearchSchema>;

export const TIMETABLE_MODES = ['group', 'teacher', 'room', 'overview'] as const;
export type TimetableMode = (typeof TIMETABLE_MODES)[number];
export const TimetableSearchSchema = z.object({
  mode: z.enum(TIMETABLE_MODES).optional().catch(undefined),
  id: uuid,
  week: z.iso.date().optional().catch(undefined),
  /** Overview: level shown (all levels when empty). */
  level: uuid,
});
export type TimetableSearch = z.infer<typeof TimetableSearchSchema>;

export const JurySearchSchema = z.object({ session: z.enum(SESSION_KINDS).optional().catch(undefined) });
export type JurySearch = z.infer<typeof JurySearchSchema>;

export const SETTINGS_SECTIONS = [
  'etablissement',
  'identite',
  'annees',
  'roles',
  'regles',
  'structure',
] as const;
export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number];
export function settingsSearch(s: { section?: unknown }): { section: SettingsSectionId } {
  const section = s.section;
  return {
    section:
      typeof section === 'string' && (SETTINGS_SECTIONS as readonly string[]).includes(section)
        ? (section as SettingsSectionId)
        : 'etablissement',
  };
}

export const AuditSearchSchema = z.object({
  entity: z.enum(AUDIT_ENTITIES).optional().catch(undefined),
  since: z.iso.date().optional().catch(undefined),
  q: text,
  page,
});
export type AuditSearch = z.infer<typeof AuditSearchSchema>;
